import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { client } from './api/client';
import { useAuthStore } from './store/authStore';

import { LandingPage } from './pages/landing/LandingPage';
import { LoginPage } from './pages/auth/LoginPage';
import { SignUpPage } from './pages/auth/SignUpPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { UpdatePasswordPage } from './pages/auth/UpdatePassword';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';

import { ClientLayout } from './components/layout/ClientLayout';
import { AdminLayout } from './components/layout/AdminLayout';

import { FeedTab } from './pages/dashboard/FeedTab';
import { ProfileTab } from './pages/dashboard/ProfileTab';
import { TrainingTab } from './pages/dashboard/TrainingTab';

import { AdminFeedTab } from './pages/admin/AdminFeedTab';
import { ClientsTab } from './pages/admin/ClientsTab';
import { UpcomingSessionsTab } from './pages/admin/UpcomingSessionsTab';
import { PastSessionsTab } from './pages/admin/PastSessionsTab';
import { ReviewsManagementTab } from './pages/admin/ReviewsManagementTab';
import { DeviceAccessTab } from './pages/admin/DeviceAccessTab';
import { AdminManagementTab } from './pages/admin/AdminManagementTab';
import SuspendedPage from "./pages/dashboard/SuspendedPage";

function AdminGuard({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore();
  if (!user) return <Navigate to="/login" />;
  if (user.role !== 'admin' && user.role !== 'superadmin') {
    return <Navigate to="/dashboard" />;
  }
  return <>{children}</>;
}

function ClientGuard({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore();
  if (!user) return <Navigate to="/login" />;
  if (user.role === 'admin' || user.role === 'superadmin') {
    return <Navigate to="/admin" />;
  }
  return <>{children}</>;
}

function App() {
  const { setUser, setChecking, isChecking } = useAuthStore();

  useEffect(() => {
    async function restoreSession() {
      try {
        const { data: { session } } = await client.auth.getSession();

        if (!session) {
          setUser(null);
          setChecking(false);
          return;
        }

        const { data: profile, error } = await client
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();

        if (error || !profile) {
          setUser(null);
          setChecking(false);
          return;
        }

        setUser({
          id: session.user.id,
          email: session.user.email,
          role: profile.role,
          adminLevel: profile.admin_level || 1,
          name: profile.full_name || '',
        });

      } catch (err) {
        console.error(err);
        setUser(null);
      } finally {
        setChecking(false);
      }
    }

    restoreSession();
  }, []);

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-nhs-blue font-medium">Loading...</div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<SignUpPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/update-password" element={<UpdatePasswordPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

        {/* Client Routes */}
        <Route
          path="/dashboard"
          element={
            <ClientGuard>
              <ClientLayout>
                <FeedTab />
              </ClientLayout>
            </ClientGuard>
          }
        />
        <Route
          path="/dashboard/suspended"
          element={
            <ClientGuard>
              
                <SuspendedPage />
             
            </ClientGuard>
          }
        />
        <Route
          path="/dashboard/profile"
          element={
            <ClientGuard>
              <ClientLayout>
                <ProfileTab />
              </ClientLayout>
            </ClientGuard>
          }
        />
        <Route
          path="/dashboard/training"
          element={
            <ClientGuard>
              <ClientLayout>
                <TrainingTab />
              </ClientLayout>
            </ClientGuard>
          }
        />

        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <AdminGuard>
              <AdminLayout>
                <AdminFeedTab />
              </AdminLayout>
            </AdminGuard>
          }
        />
        <Route
          path="/admin/clients"
          element={
            <AdminGuard>
              <AdminLayout>
                <ClientsTab />
              </AdminLayout>
            </AdminGuard>
          }
        />
        <Route
          path="/admin/upcoming"
          element={
            <AdminGuard>
              <AdminLayout>
                <UpcomingSessionsTab />
              </AdminLayout>
            </AdminGuard>
          }
        />
        <Route
          path="/admin/past"
          element={
            <AdminGuard>
              <AdminLayout>
                <PastSessionsTab />
              </AdminLayout>
            </AdminGuard>
          }
        />
        <Route
          path="/admin/reviews"
          element={
            <AdminGuard>
              <AdminLayout>
                <ReviewsManagementTab />
              </AdminLayout>
            </AdminGuard>
          }
        />
        <Route
          path="/admin/devices"
          element={
            <AdminGuard>
              <AdminLayout>
                <DeviceAccessTab />
              </AdminLayout>
            </AdminGuard>
          }
        />
        <Route
          path="/admin/admins"
          element={
            <AdminGuard>
              <AdminLayout>
                <AdminManagementTab />
              </AdminLayout>
            </AdminGuard>
          }
        />
      </Routes>
    </Router>
  );
}

export default App;